import type {
  AdminCompaniesResponse,
  AdminCreateUserBody,
  AdminUser,
  AdminUserDetail,
  AdminUserPatchBody,
  AdminUsersBulkPatchBody,
  AdminUsersListResponse,
  AdminUserUpdateBody,
  AvailableCompany,
  BulkInviteAcceptedResponse,
  BulkInviteBody,
  BulkInviteStatusResponse,
  CreateUserCompanyBody,
  UserCompaniesResponse,
  UserCompanyPatchBody,
} from '@/api/(admin)/users/types';
import { BACKEND_URL } from '@/lib/constants/api';
import { apiFetch } from '@/lib/utils/apiFetch';

/**
 * Admin endpoints live on `VITE_BACKEND_URI`, not `apiFetch`'s default
 * `VITE_API_SERVICE_URI` — see the note in `(admin)/impersonate`. Passing the
 * base explicitly is what keeps these from 404ing on the api-service host.
 */

/** The roster. Takes a pre-built query string; paging and filter JSON live in the hook. */
export async function fetchAdminUsers(
  qs: string,
): Promise<AdminUsersListResponse> {
  return apiFetch<AdminUsersListResponse>(
    `/admin/users/list?${qs}`,
    {},
    BACKEND_URL,
  );
}

export async function patchAdminUser(
  id: string,
  body: AdminUserPatchBody,
): Promise<AdminUser> {
  return apiFetch<AdminUser>(
    `/admin/users/${id}`,
    { method: 'PATCH', body: JSON.stringify(body) },
    BACKEND_URL,
  );
}

export async function bulkPatchAdminUsers(
  body: AdminUsersBulkPatchBody,
): Promise<unknown> {
  return apiFetch<unknown>(
    '/admin/users/bulk-patch',
    { method: 'PATCH', body: JSON.stringify(body) },
    BACKEND_URL,
  );
}

/** The detail page's user, with memberships and preferences. */
export async function fetchAdminUser(id: string): Promise<AdminUserDetail> {
  return apiFetch<AdminUserDetail>(`/admin/users/${id}`, {}, BACKEND_URL);
}

/**
 * Full update — and it means full.
 *
 * `updateUserFullUnscopedDb` writes every column in one unconditional `SET`,
 * and the service hands `body.firstName` etc. straight to it, so a field left
 * out of the body is written as `NULL`. `isBlocked` is worse: the service does
 * `body.isBlocked ? 1 : 0`, so omitting it unblocks the user.
 *
 * That is why the body type has no optional members. Callers must send the
 * user's current values with the edit applied on top — `useAdminUserFields`
 * does that merge, and is the only thing that should call this.
 */
export async function putAdminUser(
  id: string,
  body: AdminUserUpdateBody,
): Promise<AdminUserDetail> {
  return apiFetch<AdminUserDetail>(
    `/admin/users/${id}`,
    { method: 'PUT', body: JSON.stringify(body) },
    BACKEND_URL,
  );
}

export async function fetchUserCompanies(
  userId: string,
): Promise<UserCompaniesResponse> {
  return apiFetch<UserCompaniesResponse>(
    `/admin/user-companies/${userId}`,
    {},
    BACKEND_URL,
  );
}

/** `id` is the membership row's id, not the company's. */
export async function patchUserCompany(
  id: string,
  body: UserCompanyPatchBody,
): Promise<unknown> {
  return apiFetch<unknown>(
    `/admin/user-companies/${id}`,
    { method: 'PATCH', body: JSON.stringify(body) },
    BACKEND_URL,
  );
}

export async function deleteUserCompany(id: string): Promise<unknown> {
  return apiFetch<unknown>(
    `/admin/user-companies/${id}`,
    { method: 'DELETE' },
    BACKEND_URL,
  );
}

export async function createUserCompany(
  body: CreateUserCompanyBody,
): Promise<unknown> {
  return apiFetch<unknown>(
    '/admin/user-companies',
    { method: 'POST', body: JSON.stringify(body) },
    BACKEND_URL,
  );
}

/**
 * Companies the user is not already a member of.
 *
 * Returns a **bare array**, not the `{data, meta}` envelope the other admin
 * list endpoints use — the legacy handler sent `reply.send(result)` and the
 * contract kept that. There is no paging either, only the `q` filter, so the
 * whole set arrives in one response. Wrapping it is the hook's job.
 */
export async function fetchAvailableCompanies(
  userId: string,
  qs: string,
): Promise<AvailableCompany[]> {
  return apiFetch<AvailableCompany[]>(
    `/admin/user-companies/available-companies/${userId}?${qs}`,
    {},
    BACKEND_URL,
  );
}

/** Hard-deletes a user. Returns 204, so there is no body. */
export async function deleteAdminUser(id: string): Promise<void> {
  await apiFetch<void>(`/admin/users/${id}`, { method: 'DELETE' }, BACKEND_URL);
}

/** Creates a user and sends their invite. */
export async function createAdminUser(
  body: AdminCreateUserBody,
): Promise<AdminUser> {
  return apiFetch<AdminUser>(
    '/admin/users',
    { method: 'POST', body: JSON.stringify(body) },
    BACKEND_URL,
  );
}

/**
 * Every company, for the create-user picker. Properly paginated — `page`,
 * `count` and a JSON `filter` — unlike `available-companies`, so this one feeds
 * `usePaginatedSelect` as intended.
 *
 * The admin companies list moved from `/admin/companies/list` to
 * `/admin/companies` and its envelope from `meta.totalCount` to
 * `metadata.pagination.total` as part of the admin-companies REST redesign
 * (`(admin)/companies`). This function's four callers (tenant picker,
 * bulk-invite sheet, certification flow, create-user) all built their
 * `usePaginatedSelect` `queryFn` around the old `{data,meta:{totalCount}}`
 * shape, so the response is re-shaped back into it here rather than touching
 * every call site for an endpoint whose only consumer is this one picker.
 */
export async function fetchAdminCompanies(
  qs: string,
): Promise<AdminCompaniesResponse> {
  const response = await apiFetch<{
    data: AdminCompaniesResponse['data'];
    metadata?: { pagination?: { page: number; limit: number; total: number } };
  }>(`/admin/companies?${qs}`, {}, BACKEND_URL);
  const pagination = response.metadata?.pagination;
  return {
    data: response.data,
    meta: {
      count: response.data.length,
      page: pagination ? pagination.page - 1 : 0,
      pageSize: pagination?.limit ?? response.data.length,
      totalCount: pagination?.total ?? response.data.length,
    },
  };
}

/**
 * Queues the bulk-invite job. Returns a `requestId` to poll — the users are
 * created by a background worker, not by this call.
 */
export async function bulkInviteUsers(
  body: BulkInviteBody,
): Promise<BulkInviteAcceptedResponse> {
  return apiFetch<BulkInviteAcceptedResponse>(
    '/admin/users/bulk-invite',
    { method: 'POST', body: JSON.stringify(body) },
    BACKEND_URL,
  );
}

/** Live status of a queued bulk invite, including the report link when done. */
export async function fetchBulkInviteStatus(
  requestId: string,
): Promise<BulkInviteStatusResponse> {
  return apiFetch<BulkInviteStatusResponse>(
    `/admin/users/bulk-invite/status/${requestId}`,
    {},
    BACKEND_URL,
  );
}
